import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { claimAndPrintComande } from '../lib/comande';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { PaymentDialog } from '../components/common/PaymentDialog';
import { DiscountControl } from '../components/common/DiscountControl';
import { CustomItemDialog } from '../components/common/CustomItemDialog';
import { StatusPill } from '../components/common/StatusPill';
import { KITCHEN_STATUS, kitchenStatusLabel, fmtEur } from '../lib/constants';
import { cn } from '../lib/utils';
import { ArrowLeft, Plus, Minus, Trash2, Send, Receipt, CreditCard, History, X, Ban, StickyNote } from 'lucide-react';
import { CategoryIcon } from '../lib/categoryIcons';
import { useConfirm } from '../context/ConfirmContext';
import { requestBillAndPrint } from '../lib/receipt';

export default function OrderPage() {
  const { t } = useTranslation();
  const { tableId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [activeSub, setActiveSub] = useState('all');
  const [optProduct, setOptProduct] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [table, setTable] = useState(null);
  // Nota su un articolo del carrello (es. "senza ghiaccio"). Disponibile su tutti i profili.
  const [noteItem, setNoteItem] = useState(null);
  const [noteText, setNoteText] = useState('');
  const confirm = useConfirm();

  const loadOrder = async () => {
    try {
      const { data } = await api.get(`/orders/by-table/${tableId}`);
      setOrder(data);
    } catch (e) {
      // No order yet: keep a local empty order without creating one on the server.
      setOrder({ id: null, items: [], total: 0 });
    }
  };

  const goBack = async () => {
    if (order?.id && (order.items || []).length === 0) {
      try { await api.post(`/orders/${order.id}/cancel`); } catch (e) { /* ignore */ }
    }
    navigate('/tavoli');
  };

  useEffect(() => {
    (async () => {
      const [c, p, tb, sc] = await Promise.all([
        api.get('/categories'), api.get('/products?light=true&channel=tavoli'), api.get('/tables'), api.get('/subcategories'),
      ]);
      setCategories(c.data); setProducts(p.data); setSubcategories(sc.data);
      setTable(tb.data.find((x) => x.id === tableId));
      await loadOrder();
    })();
  }, [tableId]);

  const curSubs = useMemo(() => (activeCat === 'all' ? [] : subcategories.filter((s) => s.category_id === activeCat)), [subcategories, activeCat]);

  const filtered = useMemo(() => {
    let list = products.filter((p) => p.available);
    if (activeCat !== 'all') list = list.filter((p) => p.category_id === activeCat);
    if (activeCat !== 'all' && activeSub !== 'all') list = list.filter((p) => p.subcategory_id === activeSub);
    return list;
  }, [products, activeCat, activeSub]);

  const selectCat = (id) => { setActiveCat(id); setActiveSub('all'); };

  const addProduct = async (product, opts = {}) => {
    try {
      let ord = order;
      if (!ord?.id) {
        const { data } = await api.post('/orders/open', { table_id: tableId });
        ord = data; setOrder(data);
      }
      const { data } = await api.post(`/orders/${ord.id}/items`, {
        product_id: product.id, qty: opts.qty || 1,
        variant: opts.variant || null, extras: opts.extras || [], notes: opts.notes || '',
      });
      setOrder(data);
    } catch (e) { toast.error(t('order.addProductError')); }
  };

  const clickProduct = (product) => {
    if ((product.variants && product.variants.length) || (product.extras && product.extras.length)) {
      setOptProduct(product);
    } else {
      addProduct(product);
    }
  };

  const updateQty = async (item, delta) => {
    const nq = item.qty + delta;
    if (nq <= 0) return removeItem(item);
    const { data } = await api.put(`/orders/${order.id}/items/${item.id}`, { qty: nq });
    setOrder(data);
  };

  // Apre il dialogo per aggiungere/modificare una nota sull'articolo.
  const openNote = (item) => { setNoteItem(item); setNoteText(item.notes || ''); };
  const saveNote = async () => {
    if (!noteItem) return;
    try {
      const { data } = await api.put(`/orders/${order.id}/items/${noteItem.id}`, { notes: noteText.trim() });
      setOrder(data);
      setNoteItem(null); setNoteText('');
      toast.success(t('order.noteSaved', { defaultValue: 'Nota salvata' }));
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.addProductError')); }
  };

  const removeItem = async (item, sent = false) => {
    if (sent) {
      const ok = await confirm({
        title: t('order.voidConfirmTitle', { name: item.name }),
        description: t('order.voidConfirmDesc'),
        destructive: true, confirmText: t('order.void'),
      });
      if (!ok) return;
    }
    try {
      const { data } = await api.delete(`/orders/${order.id}/items/${item.id}`);
      setOrder(data);
      if (sent) toast.success(t('order.itemVoided'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.cannotRemove')); }
  };

  const sendKitchen = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/send`);
      setOrder(data); toast.success(t('order.orderSent'));
      claimAndPrintComande();
    } catch (e) { toast.error(e?.response?.data?.detail || t('order.noNewItems')); }
  };

  const requestBill = async () => {
    try {
      const via = await requestBillAndPrint(api, order.id);
      toast.success(via === 'agent' ? t('order.billSentToPrinter') : t('order.billPrinting'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('order.billError'));
    }
  };

  const onPaid = (res) => {
    if (res.order_closed) { toast.success(t('order.tableClosed')); navigate('/tavoli'); }
    else loadOrder();
  };

  if (!order) return <div className="p-6 text-muted-foreground">{t('order.loadingOrder')}</div>;

  const pendingCount = (order.items || []).reduce((s, i) => s + Math.max(0, Number(i.qty || 0) - Number(i.sent_qty || 0)), 0);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-border bg-card">
        <Button variant="ghost" size="icon" onClick={goBack} data-testid="order-back-button"><ArrowLeft className="h-5 w-5" /></Button>
        <div>
          <div className="font-display text-xl font-semibold">{order.table_name}</div>
          <div className="text-xs text-muted-foreground">{(order.items || []).length} {t('order.items')}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setHistoryOpen(true)} title={t('order.editHistory')}><History className="h-5 w-5" /></Button>
          <StatusPill status={table?.status === 'bill_requested' ? 'bill_requested' : (order.status === 'sent' ? 'ordered' : 'occupied')} />
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_400px] overflow-hidden">
        {/* Products */}
        <div className="flex flex-col overflow-hidden">
          <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar border-b border-border">
            <button onClick={() => setCustomOpen(true)} className="h-10 px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5 border border-dashed border-primary text-primary hover:bg-primary/10" data-testid="pos-custom-item-button"><Plus className="h-4 w-4" /> {t('order.customItem')}</button>
            <button onClick={() => selectCat('all')} className={cn('h-10 px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors', activeCat === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-secondary')}>{t('order.all')}</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => selectCat(c.id)} className={cn('h-10 px-4 rounded-lg text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5', activeCat === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-secondary')}><CategoryIcon iconKey={c.icon} color={activeCat === c.id ? undefined : c.color} className="h-4 w-4" />{c.name}</button>
            ))}
          </div>
          {curSubs.length > 0 && (
            <div className="flex gap-2 px-3 py-2 overflow-x-auto no-scrollbar border-b border-border bg-muted/30" data-testid="pos-subcategory-bar">
              <button onClick={() => setActiveSub('all')} className={cn('h-8 px-3 rounded-lg text-xs font-medium whitespace-nowrap transition-colors', activeSub === 'all' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-secondary')}>{t('subcat.allOf')}</button>
              {curSubs.map((s) => (
                <button key={s.id} onClick={() => setActiveSub(s.id)} className={cn('h-8 px-3 rounded-lg text-xs font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5', activeSub === s.id ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:bg-secondary')}><CategoryIcon iconKey={s.icon} color={activeSub === s.id ? undefined : s.color} className="h-3.5 w-3.5" />{s.name}</button>
              ))}
            </div>
          )}
          <ScrollArea className="flex-1 p-3">
            {/* Griglia responsive via stile inline (robusto in produzione, indipendente dal
                purge di Tailwind): i prodotti restano leggibili su qualsiasi schermo. */}
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
            >
              {filtered.map((p) => (
                <button key={p.id} data-testid="pos-product" onClick={() => clickProduct(p)}
                  className="h-24 rounded-xl border border-border bg-card hover:border-primary hover:shadow-md active:scale-[0.98] transition-all p-3 flex flex-col justify-between text-left">
                  <span className="font-medium text-sm leading-tight line-clamp-2">{p.name}</span>
                  <span className="font-display font-semibold text-primary tabular">{fmtEur(p.price)}</span>
                </button>
              ))}
              {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">{t('order.noProducts')}</div>}
            </div>
          </ScrollArea>
        </div>

        {/* Cart */}
        <div className="border-l border-border bg-card flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-border font-semibold">{t('order.order')}</div>
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-2">
              {(order.items || []).length === 0 && <div className="text-center text-muted-foreground py-12 text-sm">{t('order.noItems')}</div>}
              {(order.items || []).map((it) => {
                const ks = KITCHEN_STATUS[it.kitchen_status] || KITCHEN_STATUS.pending;
                const sentQty = Number(it.sent_qty || 0);
                const hasPending = Number(it.qty || 0) > sentQty;
                const anySent = sentQty > 0;
                return (
                  <div key={it.id} className="rounded-xl border border-border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{it.name}{it.variant ? ` (${it.variant})` : ''}</div>
                        {it.extras?.length > 0 && <div className="text-xs text-muted-foreground">+ {it.extras.map((e) => e.name).join(', ')}</div>}
                        {it.notes && <div className="text-xs text-accent">{t('order.note')}: {it.notes}</div>}
                        <div className={cn('text-xs mt-0.5', ks.color)}>{kitchenStatusLabel(t, it.kitchen_status)}</div>
                        {anySent && hasPending && (<div className="text-[11px] text-muted-foreground">{sentQty} {t('order.sentQty', { defaultValue: 'inviati' })} · {Number(it.qty) - sentQty} {t('order.newQty', { defaultValue: 'nuovi' })}</div>)}
                      </div>
                      <div className="text-right">
                        <div className="font-display font-semibold tabular">{fmtEur(it.line_total)}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={!hasPending} onClick={() => updateQty(it, -1)}><Minus className="h-4 w-4" /></Button>
                        <span className="w-8 text-center font-medium tabular">{it.qty}</span>
                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQty(it, 1)}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className={cn('h-8 w-8', it.notes ? 'text-accent' : 'text-muted-foreground')} disabled={anySent} onClick={() => openNote(it)} data-testid={`item-note-${it.id}`} title={t('order.addNote', { defaultValue: 'Aggiungi nota' })}><StickyNote className="h-4 w-4" /></Button>
                        {anySent
                          ? <Button variant="ghost" size="sm" className="h-8 px-2 text-destructive hover:text-destructive" data-testid="void-item" onClick={() => removeItem(it, true)}><Ban className="h-4 w-4 mr-1" /> {t('order.void')}</Button>
                          : <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeItem(it)}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="border-t border-border p-4 space-y-3">
            <DiscountControl order={order} onApplied={(o) => setOrder((prev) => ({ ...prev, ...o }))} className="pb-3 border-b border-border" />
            {Number(order.discount_amount || 0) > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('payment.subtotal', { defaultValue: 'Subtotale' })}</span>
                <span className="tabular text-muted-foreground line-through">{fmtEur(order.items_total ?? order.total)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('order.total')}</span>
              <span className="font-display text-2xl font-semibold tabular">{fmtEur(order.total)}</span>
            </div>
            <Button onClick={sendKitchen} disabled={pendingCount === 0} className="w-full h-12 rounded-xl" data-testid="pos-send-to-kitchen-button">
              <Send className="h-5 w-5 mr-2" /> {t('order.sendToKitchen')} {pendingCount > 0 ? `(${pendingCount})` : ''}
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={requestBill} disabled={(order.items || []).length === 0} className="h-11 rounded-xl" data-testid="pos-request-bill-button"><Receipt className="h-4 w-4 mr-2" /> {t('order.bill')}</Button>
              <Button onClick={() => setPayOpen(true)} disabled={(order.items || []).length === 0} className="h-11 rounded-xl"><CreditCard className="h-4 w-4 mr-2" /> {t('order.pay')}</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Product options dialog */}
      <ProductOptions product={optProduct} onClose={() => setOptProduct(null)} onConfirm={(opts) => { addProduct(optProduct, opts); setOptProduct(null); }} />

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} order={order} onPaid={onPaid} />

      <CustomItemDialog open={customOpen} onOpenChange={setCustomOpen} orderId={order?.id} onAdded={(data) => setOrder(data)} />

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('order.editHistory')}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-96">
            <div className="space-y-2">
              {(order.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <div><span className="font-medium capitalize">{h.action.replace('_', ' ')}</span> <span className="text-muted-foreground">{h.detail}</span></div>
                  <span className="text-xs text-muted-foreground">{new Date(h.at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialogo NOTA articolo (es. "senza ghiaccio") - disponibile su tutti i profili */}
      <Dialog open={!!noteItem} onOpenChange={(o) => { if (!o) { setNoteItem(null); setNoteText(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('order.noteFor', { defaultValue: 'Nota' })}{noteItem ? `: ${noteItem.name}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>{t('order.note')}</Label>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('order.notePlaceholder', { defaultValue: 'Es. senza ghiaccio, ben cotto, senza cipolla…' })}
              className="min-h-[90px]"
              data-testid="item-note-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">{t('order.noteHint', { defaultValue: 'La nota appare in cucina/bar e sul conto.' })}</p>
          </div>
          <DialogFooter>
            {noteItem?.notes ? (
              <Button variant="ghost" className="mr-auto text-destructive" onClick={() => { setNoteText(''); }} data-testid="item-note-clear">{t('common.clear', { defaultValue: 'Cancella' })}</Button>
            ) : null}
            <Button variant="outline" onClick={() => { setNoteItem(null); setNoteText(''); }}>{t('common.cancel')}</Button>
            <Button onClick={saveNote} data-testid="item-note-save">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductOptions({ product, onClose, onConfirm }) {
  const { t } = useTranslation();
  const [variant, setVariant] = useState(null);
  const [extras, setExtras] = useState({});
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (product) { setVariant(product.variants?.[0]?.name || null); setExtras({}); setQty(1); setNotes(''); }
  }, [product]);

  if (!product) return null;

  const selectedExtras = (product.extras || []).filter((e) => extras[e.name]).map((e) => ({ name: e.name, price: e.price }));

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {product.variants?.length > 0 && (
            <div>
              <Label>{t('order.variant')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {product.variants.map((v) => (
                  <button key={v.name} onClick={() => setVariant(v.name)} className={cn('h-11 rounded-lg border text-sm font-medium transition-colors', variant === v.name ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted')}>
                    {v.name} {v.price_delta ? `(+${fmtEur(v.price_delta)})` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.extras?.length > 0 && (
            <div>
              <Label>{t('order.extras')}</Label>
              <div className="space-y-1.5 mt-1.5">
                {product.extras.map((e) => (
                  <label key={e.name} className="flex items-center justify-between h-10 px-3 rounded-lg border border-border cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!extras[e.name]} onChange={(ev) => setExtras((s) => ({ ...s, [e.name]: ev.target.checked }))} /> {e.name}</span>
                    <span className="text-sm text-muted-foreground">+{fmtEur(e.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div><Label>{t('order.kitchenNotes')}</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('order.kitchenNotesPlaceholder')} className="mt-1.5" /></div>
          <div className="flex items-center gap-2">
            <Label className="mr-2">{t('order.quantity')}</Label>
            <Button variant="outline" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></Button>
            <span className="w-8 text-center font-medium">{qty}</span>
            <Button variant="outline" size="icon" onClick={() => setQty((q) => q + 1)}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => onConfirm({ variant, extras: selectedExtras, qty, notes })} data-testid="pos-add-product-button">{t('common.add')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
